import type { Horizon, Metric, ValueType } from "./types";

const UNITS: Record<string, string> = {
  "1": "",
  degC: "°C",
  "degC days": "°C·days",
  "m s-1": "m/s",
  "kg m-2": "kg/m²",
  "m3 s-1": "m³/s",
};
export const unitsOf = (m: Metric) => UNITS[m.units] ?? m.units;

/** A compact number for axes and legends: 3 or 4 significant figures. */
export function fmt(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  const a = Math.abs(v);
  if (a === 0) return "0";
  if (a >= 1e5 || a < 1e-3) return v.toExponential(1);
  return v.toLocaleString("en-GB", { maximumSignificantDigits: a >= 100 ? 4 : 3 });
}

export const signed = (v: number) => (v > 0 ? "+" : "") + fmt(v);

/**
 * A cube value as the page shows it: four significant figures, whole numbers from 1000 up, a
 * sign on a change, and a true minus sign.
 */
export function show(v: number | null | undefined, vt: ValueType): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  if (v === 0) return "0";
  const a = Math.abs(v);
  const text = a.toLocaleString("en-GB", a >= 1000 ? { maximumFractionDigits: 0 } : { maximumSignificantDigits: 4 });
  return (v < 0 ? "−" : vt === "diff" ? "+" : "") + text;
}

/** A cube value for the CSV: every digit the cube has, no thousands separators. */
export const csvNumber = (v: number | null | undefined) =>
  v === null || v === undefined || !Number.isFinite(v) ? "" : String(v);

/** The years a horizon spans, such as the baseline's 1995–2014. */
export const span = (h: Horizon) => `${h.start}–${h.end}`;

export const yearLabel = (h: Horizon) => (h.id === "baseline" ? "Baseline" : String(h.centre));
export const yearLong = (h: Horizon) =>
  h.id === "baseline" ? `Baseline (${h.start}–${h.end})` : `${h.centre} (average of ${h.start}–${h.end})`;

export const coordName = (lat: number, lon: number) =>
  `${Math.abs(lat).toFixed(2)}°${lat >= 0 ? "N" : "S"}, ${Math.abs(lon).toFixed(2)}°${lon >= 0 ? "E" : "W"}`;

/** Clean axis ticks: about `n` round numbers spanning [lo, hi]. */
export function niceTicks(lo: number, hi: number, n = 5): number[] {
  if (!(hi > lo)) hi = lo + 1;
  const raw = (hi - lo) / n;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((f) => f * mag).find((s) => s >= raw)!;
  const out: number[] = [];
  for (let v = Math.ceil(lo / step) * step; v <= hi + step * 1e-9; v += step) out.push(+v.toFixed(10));
  return out;
}

const LATLON = /^\s*(-?\d+(?:\.\d+)?)\s*[,;\s]\s*(-?\d+(?:\.\d+)?)\s*$/;

/** A typed "lat, lon" within the globe's ranges, or null. */
export function parseLatLon(text: string): { lat: number; lon: number } | null {
  const ll = text.match(LATLON);
  if (!ll) return null;
  const lat = +ll[1];
  const lon = +ll[2];
  return Math.abs(lat) <= 90 && Math.abs(lon) <= 180 ? { lat, lon } : null;
}
